import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import noimage from "../asset/noimage.png";
import { FaStar } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { encodeCourseId } from "../utils/encoder";

const InstructorCourses = () => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 10;
  const navigate = useNavigate();

  const handleCourseClick = (courseId) => {
    const encodedId = encodeCourseId(courseId);
    navigate(`/instructor-panel/course-curriculum/${encodedId}`);
  };

  useEffect(() => {
    const fetchCoursesAndCategories = async () => {
      try {
        setLoading(true);
        const instructor_id = sessionStorage.getItem("instructor_id");
        if (!instructor_id) throw new Error("Instructor ID not found in session.");

        // 1. Fetch Instructor's Course IDs
        const instructorCoursesRes = await fetch(SummaryApi.getCourseIdByInstructorId.url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ instructor_id: Number(instructor_id) }),
        });

        if (!instructorCoursesRes.ok) {
          throw new Error(`Failed to fetch instructor's courses: ${instructorCoursesRes.status}`);
        }

        const instructorCoursesData = await instructorCoursesRes.json();
        if (!instructorCoursesData.status || !Array.isArray(instructorCoursesData.data)) {
          throw new Error("Instructor course data is invalid.");
        }

        const instructorCourseIds = instructorCoursesData.data.map((c) => c.course_id);

        // 2. Fetch All Active Courses
        const activeCoursesRes = await fetch(SummaryApi.getActiveInstructorCourses.url);
        if (!activeCoursesRes.ok) {
          throw new Error(`Failed to fetch active courses: ${activeCoursesRes.status}`);
        }

        const activeCoursesData = await activeCoursesRes.json();
        if (!activeCoursesData.status || !Array.isArray(activeCoursesData.data)) {
          throw new Error("Active course data is invalid.");
        }

        // 3. Filter Active Courses to those belonging to Instructor
        const matchingCourses = activeCoursesData.data.filter((course) =>
          instructorCourseIds.includes(course.course_id)
        );

        // 4. Fetch Categories
        const categoryRes = await fetch(SummaryApi.getActiveCourseCategory.url);
        if (!categoryRes.ok) {
          throw new Error(`Failed to fetch categories: ${categoryRes.status}`);
        }

        const categoryData = await categoryRes.json();
        if (!categoryData.status) {
          throw new Error("Failed to load categories.");
        }

        // Set states
        setCourses(matchingCourses);
        setFilteredCourses(matchingCourses);
        setCategories(categoryData.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCoursesAndCategories();
  }, []);

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.category_id === categoryId);
    return category ? category.category_name : "Unknown Category";
  };

  const handleCategoryChange = (e) => {
    const selected = e.target.value;
    setSelectedCategory(selected);
    setCurrentPage(1);

    if (selected === "All") {
      setFilteredCourses(courses);
    } else {
      const selectedCategory = categories.find(
        (cat) => cat.category_name === selected
      );
      if (selectedCategory) {
        const filtered = courses.filter(
          (course) => course.category_id === selectedCategory.category_id
        );
        setFilteredCourses(filtered);
      }
    }
  };

  // Pagination logic
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses.slice(
    indexOfFirstCourse,
    indexOfLastCourse
  );
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1f4774] dark:border-white mx-auto mb-4"></div>
            <p className="text-[#1f4774] dark:text-gray-300 font-medium">Loading courses...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
        <div className="text-center text-red-500 dark:text-red-400 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      {/* Top bar with filter */}
      <div className="flex justify-end mb-6">
        <select
          value={selectedCategory}
          onChange={handleCategoryChange}
          className="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-cyan-400 transition-colors duration-200"
        >
          {/* Apply dark theme classes directly to options as well */}
          <option value="All" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">
            All Categories
          </option>
          {categories.map((cat) => (
            <option 
              key={cat.category_id} 
              value={cat.category_name}
              className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
            >
              {cat.category_name}
            </option>
          ))}
        </select>
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {currentCourses.length > 0 ? (
          currentCourses.map((course) => (
            <div
              key={course.course_id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              // onClick={() => handleCourseClick(course.course_id)}
            >
              <img
                className="w-full h-48 object-cover"
                src={
                  course.course_image1
                    ? `${SummaryApi.instructorCourseImage.url.replace(
                        "api",
                        ""
                      )}${course.course_image1.replace("/api", "")}`
                    : noimage
                }
                alt={course.course_title}
              />
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white line-clamp-2">
                    {course.course_title}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full whitespace-nowrap ml-2">
                    {getCategoryName(course.category_id)}
                  </p>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-2 line-clamp-2 text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-200">Subtitle:</span>{" "}
                  {course.subtitle}
                </p>
                <p className="text-gray-600 dark:text-gray-300 mb-2 line-clamp-3 text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-200">Description:</span>{" "}
                  {course.description}
                </p>
                
                <div className="space-y-2 mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Duration:</span>
                    <span className="text-gray-600 dark:text-gray-400">{course.course_duration}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Lessons:</span>
                    <span className="text-gray-600 dark:text-gray-400">{course.lessons_count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Difficulty:</span>
                    <span className="text-gray-600 dark:text-gray-400">{course.diffculty_level}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Language:</span>
                    <span className="text-gray-600 dark:text-gray-400">{course.language_instruction}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-200 dark:border-gray-600">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Rating:</span>
                    <span className="text-yellow-500 font-semibold flex items-center">
                      <FaStar className="mr-1" />
                      {course.rating}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-4xl mb-4">📚</div>
            <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">No courses found</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              {selectedCategory === "All" 
                ? "You haven't created any courses yet." 
                : `No courses found in the ${selectedCategory} category.`}
            </p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredCourses.length > coursesPerPage && (
        <div className="flex justify-center items-center mt-8 space-x-4">
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 1}
            className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 font-medium transition-colors duration-200 shadow-sm"
          >
            Previous
          </button>
          <span className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 font-medium transition-colors duration-200 shadow-sm"
          >
            Next
          </button>
        </div>
      )}

      {/* Optional: Show error notification if needed */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg shadow-lg z-50 max-w-sm">
          <div className="flex items-center">
            <div className="text-red-500 dark:text-red-400 mr-2">⚠️</div>
            <span className="text-sm">{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="absolute top-2 right-2 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default InstructorCourses;